import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import './AdminDashboard.css';

// Icons from Heroicons
const TruckIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const ClockIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const UsersIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
  </svg>
);

const CurrencyIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
  </svg>
);

const AdminDashboard = () => {
  const { user, supabase, logout } = useAuth();
  const navigate = useNavigate();
  
  const [dashboardData, setDashboardData] = useState({
    pendingRequests: [],
    completedRequests: [],
    activeDrivers: [],
    stats: {
      totalRequests: 0,
      completedToday: 0,
      activeDrivers: 0,
      totalRevenue: 0
    }
  });
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user?.profile?.company_id) {
      setError('User profile not loaded');
      return;
    }
    
    loadDashboardData();
    
    // Set up real-time subscriptions
    const requestsSubscription = supabase
      .channel('taxi_requests')
      .on('postgres_changes', 
        { 
          event: '*', 
          schema: 'public', 
          table: 'taxi_requests',
          filter: `company_id=eq.${user.profile.company_id}`
        },
        () => {
          loadDashboardData();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(requestsSubscription);
    };
  }, [user?.profile?.company_id]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const companyId = user.profile.company_id;

      // Load pending requests
      const { data: pendingRequests, error: pendingError } = await supabase
        .from('taxi_requests')
        .select(`
          *,
          assigned_driver:user_profiles(full_name, phone)
        `)
        .eq('company_id', companyId)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (pendingError) throw pendingError;

      // Load completed requests (today)
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const { data: completedRequests, error: completedError } = await supabase
        .from('taxi_requests')
        .select(`
          *,
          assigned_driver:user_profiles(full_name, phone)
        `)
        .eq('company_id', companyId)
        .eq('status', 'completed')
        .gte('created_at', today.toISOString())
        .order('created_at', { ascending: false });

      if (completedError) throw completedError;

      // Load active drivers
      const { data: activeDrivers, error: driversError } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('company_id', companyId)
        .eq('role', 'DRIVER')
        .eq('is_active', true);

      if (driversError) throw driversError;

      // Calculate stats
      const { data: allRequests, error: statsError } = await supabase
        .from('taxi_requests')
        .select('status, actual_fare, created_at')
        .eq('company_id', companyId);

      if (statsError) throw statsError;

      const totalRequests = allRequests.length;
      const completedToday = completedRequests.length;
      const totalRevenue = allRequests
        .filter(r => r.actual_fare)
        .reduce((sum, r) => sum + parseFloat(r.actual_fare), 0);

      setDashboardData({
        pendingRequests: pendingRequests || [],
        completedRequests: completedRequests || [],
        activeDrivers: activeDrivers || [],
        stats: {
          totalRequests,
          completedToday,
          activeDrivers: activeDrivers?.length || 0,
          totalRevenue
        }
      });

    } catch (error) {
      console.error('Error loading dashboard data:', error);
      setError('Chyba pri načítavaní dát: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const assignDriver = async (requestId, driverId) => {
    try {
      const { error } = await supabase
        .from('taxi_requests')
        .update({
          assigned_driver_id: driverId,
          status: 'assigned'
        })
        .eq('id', requestId);

      if (error) throw error;

      // Reload data to reflect changes
      loadDashboardData();
    } catch (error) {
      console.error('Error assigning driver:', error);
      setError('Chyba pri priraďovaní vodiča: ' + error.message);
    }
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString('sk-SK', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('sk-SK', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="admin-dashboard">
        <Header />
        <div className="loading-container">
          <div className="loading-spinner">Načítavam...</div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <Header />
      
      <div className="dashboard-container">
        <div className="dashboard-header">
          <h1>Admin Dashboard</h1>
          <div className="company-info">
            <h2>{user?.profile?.companies?.name || 'Vaša taxi firma'}</h2>
            <p>Vitajte, {user?.profile?.full_name}</p>
          </div>
        </div>

        {error && (
          <div className="error-banner">
            {error}
          </div>
        )}

        {/* Statistics Cards */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">
              <ClockIcon />
            </div>
            <div className="stat-content">
              <h3>Čakajúce objednávky</h3>
              <p className="stat-number">{dashboardData.stats.totalRequests}</p>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon">
              <TruckIcon />
            </div>
            <div className="stat-content">
              <h3>Dokončené dnes</h3>
              <p className="stat-number">{dashboardData.stats.completedToday}</p>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon">
              <UsersIcon />
            </div>
            <div className="stat-content">
              <h3>Aktívni vodiči</h3>
              <p className="stat-number">{dashboardData.stats.activeDrivers}</p>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon">
              <CurrencyIcon />
            </div>
            <div className="stat-content">
              <h3>Celkové tržby</h3>
              <p className="stat-number">{formatCurrency(dashboardData.stats.totalRevenue)}</p>
            </div>
          </div>
        </div>

        {/* Pending Requests */}
        <div className="dashboard-section">
          <h2>Čakajúce objednávky</h2>
          <div className="requests-grid">
            {dashboardData.pendingRequests.length === 0 ? (
              <p className="no-data">Žiadne čakajúce objednávky</p>
            ) : (
              dashboardData.pendingRequests.map((request) => (
                <div key={request.id} className="request-card">
                  <div className="request-header">
                    <span className="request-time">{formatTime(request.created_at)}</span>
                    <span className={`status-badge status-${request.status}`}>
                      {request.status}
                    </span>
                  </div>
                  
                  <div className="request-content">
                    <div className="location-info">
                      <strong>Z:</strong> {request.pickup_location}
                    </div>
                    {request.destination_location && (
                      <div className="location-info">
                        <strong>Do:</strong> {request.destination_location}
                      </div>
                    )}
                    <div className="customer-info">
                      <strong>Zákazník:</strong> {request.customer_phone}
                    </div>
                  </div>
                  
                  <div className="request-actions">
                    <select 
                      onChange={(e) => e.target.value && assignDriver(request.id, e.target.value)}
                      defaultValue=""
                    >
                      <option value="">Priradiť vodiča...</option>
                      {dashboardData.activeDrivers.map((driver) => (
                        <option key={driver.id} value={driver.id}>
                          {driver.full_name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Completed Requests Today */}
        <div className="dashboard-section">
          <h2>Dokončené dnes</h2>
          <div className="requests-grid">
            {dashboardData.completedRequests.length === 0 ? (
              <p className="no-data">Žiadne dokončené objednávky dnes</p>
            ) : (
              dashboardData.completedRequests.map((request) => (
                <div key={request.id} className="request-card completed">
                  <div className="request-header">
                    <span className="request-time">{formatTime(request.created_at)}</span>
                    <span className={`status-badge status-${request.status}`}>
                      Dokončené
                    </span>
                  </div>
                  
                  <div className="request-content">
                    <div className="location-info">
                      <strong>Z:</strong> {request.pickup_location}
                    </div>
                    {request.destination_location && (
                      <div className="location-info">
                        <strong>Do:</strong> {request.destination_location}
                      </div>
                    )}
                    <div className="driver-info">
                      <strong>Vodič:</strong> {request.assigned_driver?.full_name || 'Nepriradený'}
                    </div>
                    {request.actual_fare && (
                      <div className="fare-info">
                        <strong>Cena:</strong> {formatCurrency(request.actual_fare)}
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Active Drivers */}
        <div className="dashboard-section">
          <h2>Aktívni vodiči</h2>
          <div className="drivers-grid">
            {dashboardData.activeDrivers.length === 0 ? (
              <p className="no-data">Žiadni aktívni vodiči</p>
            ) : (
              dashboardData.activeDrivers.map((driver) => (
                <div key={driver.id} className="driver-card">
                  <div className="driver-info">
                    <h3>{driver.full_name}</h3>
                    <p>{driver.phone}</p>
                    {driver.vehicle_model && (
                      <p className="vehicle-info">
                        {driver.vehicle_model} ({driver.vehicle_plate})
                      </p>
                    )}
                  </div>
                  <div className="driver-status online">
                    Dostupný
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default AdminDashboard; 